<script setup lang="ts">
/**
 * A role's KPIs on its page (#901): the job's own numbers, each serving a team's KPI, read like every
 * other layer's (TaskKpiRows.vue). Anyone here may propose one; a person signs it off, setting its target, cadence
 * and source as they do, and until then it draws no light. Withdrawing one keeps its reading history.
 *
 * The server decides all of this again on every call (`RoleKpisService`): the form only offers what it allows.
 */
import type { KpiSourceKind } from '@zerohuman/shared'
import { Button, ZhConfirmButton, ZhSectionHeading } from '@zerohuman/ui'
import { computed, reactive, ref, watch } from 'vue'
import { api } from '@/api'
import { type EnterpriseUserRow, usersApi } from '@/api-users'
import TaskKpiRows from '@/components/TaskKpiRows.vue'
import { KPI_DIRECTION_OPTIONS } from '@/lib/kpi'
import { type RoleKpi, type RoleKpiInput, roleKpisApi } from '@/role-kpis-api'

const props = defineProps<{ roleId: string }>()

/** How often a KPI is read, as the form offers it. A cadence set some other way is kept and offered too. */
const CADENCES: ReadonlyArray<{ minutes: number; label: string }> = [
    { minutes: 60, label: 'Hourly' },
    { minutes: 1440, label: 'Daily' },
    { minutes: 10080, label: 'Weekly' },
    { minutes: 43200, label: 'Every 30 days' },
]

const kpis = ref<RoleKpi[]>([])
const error = ref<string | null>(null)
const busy = ref(false)
/** What the form is doing: adding a KPI, or signing one off. Null while it is closed. */
const mode = ref<{ kind: 'define' } | { kind: 'signOff'; kpi: RoleKpi } | null>(null)
const teamKpis = ref<Array<{ id: string; name: string }>>([])
const tasks = ref<Array<{ id: string; name: string }>>([])
const people = ref<EnterpriseUserRow[]>([])
// A number input's v-model hands back a number once typed into, so both are read through `String()`.
const form = reactive({
    name: '',
    targetValue: '' as string | number,
    targetDirection: 'at_least' as 'at_least' | 'at_most',
    amberBand: '' as string | number,
    onTargetWorseningRule: false,
    cadenceMinutes: 10080,
    servesKpiId: '',
    sourceKind: 'task' as KpiSourceKind,
    sourceRef: '',
})

const cadences = computed(() =>
    CADENCES.some(option => option.minutes === form.cadenceMinutes)
        ? CADENCES
        : [...CADENCES, { minutes: form.cadenceMinutes, label: `Every ${form.cadenceMinutes} minutes` }],
)
const sources = computed(() =>
    form.sourceKind === 'task' ? tasks.value : people.value.map(person => ({ id: person.id, name: person.name })),
)

watch(
    () => props.roleId,
    async roleId => {
        kpis.value = []
        error.value = null
        mode.value = null
        try {
            const loaded = await roleKpisApi.forRole(roleId)
            if (roleId === props.roleId) kpis.value = loaded
        } catch {
            if (roleId === props.roleId) error.value = 'Could not load this role’s KPIs.'
        }
    },
    { immediate: true },
)

/** Opens the form, to add a KPI or to sign `kpi` off, and loads what it can serve and where readings can come from. */
async function openForm(kpi: RoleKpi | null): Promise<void> {
    mode.value = kpi ? { kind: 'signOff', kpi } : { kind: 'define' }
    error.value = null
    Object.assign(form, {
        name: kpi?.name ?? '',
        targetValue: kpi?.targetValue === null || !kpi ? '' : String(kpi.targetValue),
        targetDirection: kpi?.targetDirection ?? 'at_least',
        amberBand: kpi?.amberBand === null || !kpi ? '' : String(kpi.amberBand),
        onTargetWorseningRule: kpi?.onTargetWorseningRule === true,
        cadenceMinutes: kpi?.cadenceMinutes ?? 10080,
        servesKpiId: kpi?.servesKpiId ?? '',
        sourceKind: kpi?.sourceKind ?? 'task',
        sourceRef: kpi?.sourceRef ?? '',
    })
    const [taskRows, userRows, teams] = await Promise.all([
        api.tasks({ limit: 200 }).catch(() => null),
        usersApi.users().catch(() => []),
        api.teams().catch(() => []),
    ])
    tasks.value = (taskRows?.data ?? []).map(task => ({ id: task.id, name: task.name || task.slug }))
    people.value = userRows
    // A role is held across teams, so its KPI may serve any team's: every team's KPIs, each named for its team.
    const trees = await Promise.all(teams.map(team => api.teamKpis(team.id).catch(() => null)))
    teamKpis.value = teams.flatMap((team, index) =>
        (trees[index]?.kpis ?? []).map(teamKpi => ({ id: teamKpi.id, name: `${team.name} · ${teamKpi.name}` })),
    )
}

/** A refusal in words the person can act on; anything unexpected is shown as the server said it. */
function describe(message: string): string {
    if (message.startsWith('kpi_sign_off_incomplete:')) {
        const missing = message.slice('kpi_sign_off_incomplete:'.length).split(',').join(', ')
        return `Set the ${missing} before signing off.`
    }
    if (message === 'role_kpi_serves_non_team') {
        return 'A role’s KPI can only serve a team’s KPI.'
    }
    if (message === 'role_kpi_source_not_found') {
        return 'That source is not in this enterprise any more. Choose another.'
    }
    return message
}

/** What the form reads the KPI against, as the API takes it. */
type ReadAgainst = Pick<
    RoleKpiInput,
    'targetValue' | 'targetDirection' | 'amberBand' | 'cadenceMinutes' | 'sourceKind' | 'sourceRef'
>

/** The form's target, band, cadence and source, or what is wrong with them for `kind`. */
function readAgainstOf(kind: 'define' | 'signOff'): ReadAgainst | string {
    const target = String(form.targetValue).trim()
    const band = String(form.amberBand).trim()
    if (target !== '' && !Number.isFinite(Number(target))) {
        return 'The target must be a number.'
    }
    if (kind === 'signOff' && (target === '' || !form.sourceRef)) {
        return 'Set a target and a source before signing off.'
    }
    return {
        targetValue: target === '' ? null : Number(target),
        targetDirection: target === '' ? null : form.targetDirection,
        amberBand: band === '' ? null : Number(band),
        cadenceMinutes: form.cadenceMinutes,
        sourceKind: form.sourceRef ? form.sourceKind : null,
        sourceRef: form.sourceRef || null,
    }
}

async function submit(): Promise<void> {
    const current = mode.value
    if (!current || busy.value) {
        return
    }
    const readAgainst = readAgainstOf(current.kind)
    if (typeof readAgainst === 'string') {
        error.value = readAgainst
        return
    }
    busy.value = true
    error.value = null
    try {
        kpis.value =
            current.kind === 'define'
                ? await roleKpisApi.define(props.roleId, {
                      name: form.name.trim(),
                      ...readAgainst,
                      onTargetWorseningRule: form.onTargetWorseningRule,
                      servesKpiId: form.servesKpiId || null,
                  })
                : await roleKpisApi.signOff(props.roleId, current.kpi.id, readAgainst)
        mode.value = null
    } catch (caught) {
        error.value = describe(caught instanceof Error ? caught.message : String(caught))
    } finally {
        busy.value = false
    }
}

async function withdraw(kpi: RoleKpi): Promise<void> {
    if (busy.value) {
        return
    }
    busy.value = true
    error.value = null
    try {
        kpis.value = await roleKpisApi.withdraw(props.roleId, kpi.id)
        if (mode.value?.kind === 'signOff' && mode.value.kpi.id === kpi.id) {
            mode.value = null
        }
    } catch (caught) {
        error.value = describe(caught instanceof Error ? caught.message : String(caught))
    } finally {
        busy.value = false
    }
}
</script>

<template>
	<section class="space-y-2" data-testid="role-kpis" aria-labelledby="role-kpis-heading">
		<div class="flex flex-wrap items-center justify-between gap-2">
			<div>
				<ZhSectionHeading id="role-kpis-heading">KPIs</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					This role’s own numbers, each serving a team’s KPI. They show on that team’s page once signed off.
				</p>
			</div>
			<Button
				type="button"
				size="sm"
				variant="outline"
				data-testid="role-kpi-add"
				:disabled="busy"
				@click="openForm(null)"
			>
				Add KPI
			</Button>
		</div>
		<TaskKpiRows v-if="kpis.length" :kpis="kpis">
			<template #actions="{ kpi }">
				<span class="flex gap-2">
					<Button
						v-if="!kpi.signedOff"
						type="button"
						size="sm"
						variant="outline"
						:data-testid="`role-kpi-sign-off-${kpi.id}`"
						:disabled="busy"
						@click="openForm(kpi)"
					>
						Sign off
					</Button>
					<ZhConfirmButton
						label="Withdraw"
						confirm-label="Withdraw KPI"
						:title="`Withdraw ${kpi.name}?`"
						description="The role no longer holds this KPI. Its reading history is kept."
						:disabled="busy"
						:data-testid="`role-kpi-withdraw-${kpi.id}`"
						@confirm="withdraw(kpi)"
					/>
				</span>
			</template>
		</TaskKpiRows>
		<p v-else-if="!error" class="text-muted-foreground text-sm">This role holds no KPIs yet.</p>

		<form
			v-if="mode"
			data-testid="role-kpi-form"
			class="mt-3 grid gap-3 rounded-md border p-3 sm:grid-cols-2"
			@submit.prevent="submit"
		>
			<p class="text-sm font-medium sm:col-span-2">
				{{ mode.kind === 'define' ? 'Add a KPI to this role' : `Sign off “${mode.kpi.name}”` }}
			</p>
			<template v-if="mode.kind === 'define'">
				<label class="text-sm sm:col-span-2">
					Name
					<input
						v-model="form.name"
						data-testid="role-kpi-name"
						required
						maxlength="200"
						class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1"
					/>
				</label>
				<label class="text-sm sm:col-span-2">
					Serves
					<select
						v-model="form.servesKpiId"
						data-testid="role-kpi-serves"
						class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1"
					>
						<option value="">No team KPI</option>
						<option v-for="teamKpi in teamKpis" :key="teamKpi.id" :value="teamKpi.id">{{ teamKpi.name }}</option>
					</select>
				</label>
			</template>
			<label class="text-sm">
				Target
				<span class="mt-1 flex gap-2">
					<select
						v-model="form.targetDirection"
						data-testid="role-kpi-direction"
						class="border-input bg-background rounded-md border px-2 py-1"
					>
						<option v-for="direction in KPI_DIRECTION_OPTIONS" :key="direction.value" :value="direction.value">
							{{ direction.label }}
						</option>
					</select>
					<input
						v-model="form.targetValue"
						data-testid="role-kpi-target"
						type="number"
						step="any"
						class="border-input bg-background w-full rounded-md border px-2 py-1"
					/>
				</span>
			</label>
			<label class="text-sm">
				AMBER band (optional)
				<input
					v-model="form.amberBand"
					data-testid="role-kpi-band"
					type="number"
					step="any"
					min="0"
					class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1"
				/>
			</label>
			<label v-if="mode.kind === 'define'" class="flex items-center gap-2 text-sm sm:col-span-2">
				<input v-model="form.onTargetWorseningRule" data-testid="role-kpi-worsening" type="checkbox" />
				AMBER when on target but worse than the reading before
			</label>
			<label class="text-sm">
				Read
				<select
					v-model.number="form.cadenceMinutes"
					data-testid="role-kpi-cadence"
					class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1"
				>
					<option v-for="cadence in cadences" :key="cadence.minutes" :value="cadence.minutes">
						{{ cadence.label }}
					</option>
				</select>
			</label>
			<label class="text-sm">
				Source
				<span class="mt-1 flex gap-2">
					<select
						v-model="form.sourceKind"
						data-testid="role-kpi-source-kind"
						class="border-input bg-background rounded-md border px-2 py-1"
						@change="form.sourceRef = ''"
					>
						<option value="task">A task records it</option>
						<option value="person">A person enters it</option>
					</select>
					<select
						v-model="form.sourceRef"
						data-testid="role-kpi-source"
						class="border-input bg-background w-full rounded-md border px-2 py-1"
					>
						<option value="">Choose…</option>
						<option v-for="source in sources" :key="source.id" :value="source.id">{{ source.name }}</option>
					</select>
				</span>
			</label>
			<div class="flex gap-2 sm:col-span-2">
				<Button type="submit" size="sm" data-testid="role-kpi-submit" :disabled="busy">
					{{ mode.kind === 'define' ? 'Add KPI' : 'Sign off' }}
				</Button>
				<Button type="button" size="sm" variant="outline" :disabled="busy" @click="mode = null">Cancel</Button>
			</div>
		</form>
		<p v-if="error" data-testid="role-kpi-error" class="text-destructive text-sm">{{ error }}</p>
	</section>
</template>
